import { createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';

export type MfaMethod = 'sms' | 'totp';
export type MfaBusyState = 'send' | 'verify' | null;

export interface MfaState {
  busy: MfaBusyState;
  errorMessage: string | null;
  method: MfaMethod;
  otpCode: string;
  phoneCountryIso2: string | null;
  phoneE164: string;
  phoneValue: string;
  smsAlreadyHaveCode: boolean;
  smsMethodId: string | null;
  totpCode: string;
  totpId: string | null;
  totpQrCode: string | null;
  totpRecoveryCodes: string[] | null;
}

const initialState: MfaState = {
  busy: null,
  errorMessage: null,
  method: 'sms',
  otpCode: '',
  phoneCountryIso2: null,
  phoneE164: '',
  phoneValue: '',
  smsAlreadyHaveCode: false,
  smsMethodId: null,
  totpCode: '',
  totpId: null,
  totpQrCode: null,
  totpRecoveryCodes: null,
};

const mfaSlice = createSlice({
  initialState,
  name: 'mfa',
  reducers: {
    clearErrorMessage: (state) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.errorMessage = null;
    },
    resetSmsState: (state) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.smsMethodId = null;
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.smsAlreadyHaveCode = false;
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.otpCode = '';
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.errorMessage = null;
    },
    setBusy: (state, action: PayloadAction<MfaBusyState>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.busy = action.payload;
    },
    setErrorMessage: (state, action: PayloadAction<string | null>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.errorMessage = action.payload;
    },
    setMethod: (state, action: PayloadAction<MfaMethod>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.method = action.payload;
    },
    setOtpCode: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.otpCode = action.payload;
    },
    setPhone: (
      state,
      action: PayloadAction<{
        phoneCountryIso2: string | null;
        phoneE164: string;
        phoneValue: string;
      }>,
    ) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.phoneE164 = action.payload.phoneE164;
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.phoneValue = action.payload.phoneValue;
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.phoneCountryIso2 = action.payload.phoneCountryIso2;
    },
    setSmsAlreadyHaveCode: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.smsAlreadyHaveCode = action.payload;
    },
    setSmsMethodId: (state, action: PayloadAction<string | null>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.smsMethodId = action.payload;
    },
    setTotpCode: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.totpCode = action.payload;
    },
    setTotpId: (state, action: PayloadAction<string | null>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.totpId = action.payload;
    },
    setTotpQrCode: (state, action: PayloadAction<string | null>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.totpQrCode = action.payload;
    },
    setTotpRecoveryCodes: (state, action: PayloadAction<string[] | null>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.totpRecoveryCodes = action.payload;
    },
  },
});

export const {
  clearErrorMessage,
  resetSmsState,
  setBusy,
  setErrorMessage,
  setMethod,
  setOtpCode,
  setPhone,
  setSmsAlreadyHaveCode,
  setSmsMethodId,
  setTotpCode,
  setTotpId,
  setTotpQrCode,
  setTotpRecoveryCodes,
} = mfaSlice.actions;

export default mfaSlice.reducer;
